
import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, onValue, get, update } from 'firebase/database';
import { useAuth } from '../context/AuthContext';
import MainLayout from './MainLayout';
import 'bootstrap/dist/css/bootstrap.min.css';

// Helper functions for class promotion logic
const sss2Options = ['SSS 2 ARTS', 'SSS 2 SCIENCE', 'SSS 2 COMMERCIAL'];
const sss3Options = ['SSS 3 ARTS', 'SSS 3 SCIENCE', 'SSS 3 COMMERCIAL'];
const gradOption = ['Graduated'];

const getNextClassOptions = (currentClass) => {
    if (!currentClass) return [];
    const upperCaseClass = currentClass.toUpperCase();

    if (upperCaseClass === 'SSS 1') return sss2Options;
    if (upperCaseClass.startsWith('SSS 2')) return sss3Options;
    if (upperCaseClass.startsWith('SSS 3')) return gradOption;

    if (upperCaseClass.includes('PRIMARY')) {
        const level = parseInt(upperCaseClass.replace(/[^0-9]/g, ''), 10);
        if (level < 6) return [`PRIMARY ${level + 1}`];
        return ['JSS 1'];
    }
    if (upperCaseClass.includes('JSS')) {
        const level = parseInt(upperCaseClass.replace(/[^0-9]/g, ''), 10);
        if (level < 3) return [`JSS ${level + 1}`];
        return ['SSS 1'];
    }
    if (upperCaseClass === 'NURSERY 2') return ['PRIMARY 1'];
    if (upperCaseClass.includes('NURSERY')) {
        const level = parseInt(upperCaseClass.replace(/[^0-9]/g, ''), 10);
        if (level < 2) return [`NURSERY ${level + 1}`];
        return ['PRIMARY 1'];
    }
    if (upperCaseClass.includes('KG')) {
        const level = parseInt(upperCaseClass.replace(/[^0-9]/g, ''), 10);
        if (level < 2) return [`KG ${level + 1}`];
        return ['NURSERY 1'];
    }
    
    return gradOption; // Default fallback
};


const PromoteStudents = () => {
    const { currentUser } = useAuth();
    const [userData, setUserData] = useState(null);
    const [allClasses, setAllClasses] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [fromSession, setFromSession] = useState('');
    const [toSession, setToSession] = useState('');
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const db = getDatabase();
    
    useEffect(() => {
        if (!currentUser) return;
        
        const userRef = ref(db, `users/${currentUser.uid}`);
        const classesRef = ref(db, 'classes');
        const sessionsRef = ref(db, 'sessions'); 

        const userUnsub = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setUserData(data);
                if (data.role?.toLowerCase() === 'form-master' && data.assignedClass) {
                    setSelectedClass(data.assignedClass);
                }
            }
        });

        const classesUnsub = onValue(classesRef, (snapshot) => {
            if (snapshot.exists()) {
                setAllClasses(Object.keys(snapshot.val()).sort() || []);
            } else {
                const schoolInfoRef = ref(db, 'school_info/classes');
                onValue(schoolInfoRef, (snap) => {
                  if(snap.exists()) setAllClasses(snap.val().sort() || []);
                })
            }
        });
        
        const sessionsUnsub = onValue(sessionsRef, (snapshot) => {
            let sessionList = [];
            if (snapshot.exists()) {
              sessionList = Object.keys(snapshot.val());
            }
            
            const currentYear = new Date().getFullYear();
            const futureYear = 2071;
            const generatedSessions = [];
            for (let i = currentYear; i <= futureYear; i++) {
                generatedSessions.push(`${i}/${i + 1}`);
            }

            const allSessions = [...new Set([...sessionList, ...generatedSessions])].sort();
            setSessions(allSessions);

            if (allSessions.length > 0) {
              const latestSession = allSessions.find(s => s.startsWith(currentYear)) || allSessions[allSessions.length - 1];
              setFromSession(latestSession);
              const [startYear, endYear] = latestSession.split('/').map(Number);
              setToSession(`${startYear + 1}/${endYear + 1}`);
            }
        });

        return () => {
            userUnsub();
            classesUnsub();
            sessionsUnsub();
        };
    }, [currentUser, db]);

    const fetchStudentsAndResults = useCallback(async (className, session) => {
        if (!className || !session) return;
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const usersRef = ref(db, 'users');
            const snapshot = await get(usersRef);
            if (!snapshot.exists()) {
                setStudents([]);
                throw new Error("No users found.");
            }
            
            const allUsers = snapshot.val();
            const normalizedClassName = className.trim().toLowerCase();
            const classStudents = Object.entries(allUsers)
                .filter(([, user]) => 
                    user.role?.toLowerCase() === 'student' && 
                    (user.class?.trim().toLowerCase() === normalizedClassName || user.studentClass?.trim().toLowerCase() === normalizedClassName)
                )
                .map(([uid, data]) => ({ uid, ...data }));

            const sessionPath = session.replace('/', '-');
            const classPath = className.replace(/\s+/g, '').toLowerCase();

            const studentsWithResults = await Promise.all(
                classStudents.map(async (student) => {
                    const firstTermRef = ref(db, `master_report_sheets/${sessionPath}/FIRST TERM/${classPath}/${student.uid}`);
                    const secondTermRef = ref(db, `master_report_sheets/${sessionPath}/SECOND TERM/${classPath}/${student.uid}`);
                    const thirdTermRef = ref(db, `master_report_sheets/${sessionPath}/THIRD TERM/${classPath}/${student.uid}`);

                    const [firstTermSnap, secondTermSnap, thirdTermSnap] = await Promise.all([
                        get(firstTermRef),
                        get(secondTermRef),
                        get(thirdTermRef)
                    ]);

                    if (!firstTermSnap.exists() || !secondTermSnap.exists() || !thirdTermSnap.exists()) {
                        return null;
                    }

                    const reportSnap = thirdTermSnap;
                    let cumulativeAverage = 0;
                    const termAverages = reportSnap.exists() ? reportSnap.val().termAverages : {};
                    const validTerms = Object.values(termAverages || {}).filter(t => t.average > 0);
                    if (validTerms.length > 0) {
                        const totalAverage = validTerms.reduce((sum, term) => sum + term.average, 0);
                        cumulativeAverage = totalAverage / validTerms.length;
                    }
                    
                    const canBePromoted = cumulativeAverage >= 50;
                    const nextClassOptions = getNextClassOptions(student.class || student.studentClass);
                    
                    return { 
                        ...student, 
                        cumulativeAverage, 
                        canBePromoted, 
                        promoteTo: canBePromoted && nextClassOptions.length > 0 ? nextClassOptions[0] : 'Not Promoted',
                        nextClassOptions
                    };
                })
            );
            
            setStudents(studentsWithResults.filter(Boolean));

        } catch (err) {
            setError(`Failed to fetch students or results: ${err.message}`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [db]);

    useEffect(() => {
        if (selectedClass && fromSession) {
            fetchStudentsAndResults(selectedClass, fromSession);
        }
    }, [selectedClass, fromSession, fetchStudentsAndResults]);

    const handleStudentPromotionChange = (studentId, newClass) => {
        setStudents(prevStudents => prevStudents.map(student =>
            student.uid === studentId ? { ...student, promoteTo: newClass } : student
        ));
    };

    const handlePromote = async () => {
        if (!toSession) {
            setError('Please select the session to promote students into.');
            return;
        }

        const studentsToPromote = students.filter(s => s.canBePromoted && s.promoteTo && s.promoteTo !== 'Not Promoted' && s.promoteTo !== 'Graduated');
        const studentsToGraduate = students.filter(s => s.canBePromoted && s.promoteTo === 'Graduated');

        if (studentsToPromote.length === 0 && studentsToGraduate.length === 0) {
            setError('No eligible students selected for promotion or graduation.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const updates = {};
        const promotionTimestamp = new Date().toISOString();
        const promotedBy = currentUser.uid;
        const fromSessionPath = fromSession.replace('/', '-');

        studentsToPromote.forEach(student => {
            const newClass = student.promoteTo;
            updates[`/users/${student.uid}/class`] = newClass;
            updates[`/users/${student.uid}/studentClass`] = newClass;
            updates[`/users/${student.uid}/session`] = toSession;
            updates[`/users/${student.uid}/term`] = 'FIRST TERM';
            
            updates[`/promotionHistory/${fromSessionPath}/${selectedClass.replace(/\s+/g, '').toLowerCase()}/${student.uid}`] = {
                fromClass: selectedClass,
                toClass: newClass,
                fromSession: fromSession,
                toSession: toSession,
                promotedOn: promotionTimestamp,
                promotedBy,
                status: 'Promoted'
            };
        });

        studentsToGraduate.forEach(student => {
            updates[`/users/${student.uid}/status`] = 'Graduated';
            updates[`/users/${student.uid}/class`] = 'Graduated';
            updates[`/users/${student.uid}/studentClass`] = 'Graduated';
            
            updates[`/promotionHistory/${fromSessionPath}/${selectedClass.replace(/\s+/g, '').toLowerCase()}/${student.uid}`] = {
                fromClass: selectedClass,
                toClass: 'Graduated',
                fromSession: fromSession,
                toSession: toSession,
                promotedOn: promotionTimestamp,
                promotedBy,
                status: 'Graduated'
            };
        });

        try {
            await update(ref(db), updates);
            let successMsg = '';
            if (studentsToPromote.length > 0) successMsg += `Successfully promoted ${studentsToPromote.length} students. `;
            if (studentsToGraduate.length > 0) successMsg += `Successfully graduated ${studentsToGraduate.length} students. `;
            setSuccess(successMsg.trim());

            setStudents([]);
        } catch (err) {
            setError(`An error occurred during the process: ${err.message}`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const isFormMaster = userData?.role?.toLowerCase() === 'form-master';
    const isAdminOrPrincipal = userData?.role?.toLowerCase() === 'admin' || userData?.role?.toLowerCase() === 'principal';

    return (
        <MainLayout>
            <div className="container-fluid">
                <h3 className="mt-4">Promote Students</h3>
                <p>Promote or graduate students based on their Third Term cumulative average for the selected session.</p>

                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {success && <div className="alert alert-success" role="alert">{success}</div>}

                <div className="card">
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label htmlFor="classSelect">Promote from Class</label>
                                {(isAdminOrPrincipal) && (
                                    <select id="classSelect" className="form-select" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} disabled={loading}>
                                        <option value="">-- Select Class --</option>
                                        {allClasses.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                )}
                                {isFormMaster && (
                                    <input 
                                        type="text" 
                                        id="classSelect"
                                        className="form-control"
                                        value={selectedClass} 
                                        readOnly 
                                    />
                                )}
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="fromSessionSelect">From Session (Third Term)</label>
                                <select id="fromSessionSelect" className="form-select" value={fromSession} onChange={(e) => setFromSession(e.target.value)} disabled={loading}>
                                    {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>

                        {loading && <div className="text-center"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>}
                        
                        {students.length > 0 && !loading && (
                            <>
                                <h5 className="mt-4">Students in {selectedClass} for {fromSession}</h5>
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Student Name</th>
                                                <th>3rd Term Avg.</th>
                                                <th>Promotion Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {students.map(student => (
                                                <tr key={student.uid}>
                                                    <td>{student.fullName || student.name}</td>
                                                    <td>{student.cumulativeAverage.toFixed(2)}</td>
                                                    <td>
                                                        {student.canBePromoted 
                                                            ? <span className="badge bg-success">Eligible</span> 
                                                            : <span className="badge bg-danger">Ineligible</span>}
                                                    </td>
                                                    <td>
                                                        {student.canBePromoted ? (
                                                            <select 
                                                                className="form-select form-select-sm"
                                                                value={student.promoteTo}
                                                                onChange={(e) => handleStudentPromotionChange(student.uid, e.target.value)}
                                                            >
                                                                {student.nextClassOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                                                <option value="Not Promoted">Don't Promote</option>
                                                            </select>
                                                        ) : (
                                                            <span className="text-muted">Not Promoted</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-4 p-3 bg-light rounded">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label htmlFor="toSessionSelect">Promote into Session (First Term)</label>
                                            <select id="toSessionSelect" className="form-select" value={toSession} onChange={(e) => setToSession(e.target.value)}>
                                                <option value="">-- Select New Session --</option>
                                                {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-6 align-self-end">
                                            <button className="btn btn-primary w-100" onClick={handlePromote} disabled={loading || !toSession}>
                                                {loading ? 'Processing...' : 'Confirm & Process Promotions / Graduations'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                         {!loading && students.length === 0 && selectedClass && fromSession && (
                           <div className="alert alert-info mt-4">No students found for the selected class and session. They may have already been promoted or are missing results for one or more terms.</div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default PromoteStudents;
